<template>
  <el-dialog
    :visible.sync="visibleProxy"
    :title="title"
    :width="width"
    :top="top"
    :before-close="handleBeforeClose"
    :close-on-click-modal="closeOnClickModal"
    :append-to-body="appendToBody"
    :destroy-on-close="destroyOnClose"
    :custom-class="dialogClassComputed"
    @open="$emit('open')"
    @closed="$emit('closed')"
  >
    <div :class="contentClassComputed">
      <div v-if="usePanel" :class="['sk-panel', panelVariantClass]">
        <div v-if="showPanelHead" class="sk-panel-head">
          <div v-if="showPanelIcon" :class="['sk-panel-icon', panelIconToneClass]">
            <slot name="panel-icon">
              <i :class="panelIcon"></i>
            </slot>
          </div>
          <div>
            <p v-if="$slots['panel-title'] || panelTitle" class="sk-panel-title">
              <slot name="panel-title">{{ panelTitle }}</slot>
            </p>
            <p v-if="$slots['panel-subtitle'] || panelSubtitle" class="sk-panel-subtitle">
              <slot name="panel-subtitle">{{ panelSubtitle }}</slot>
            </p>
            <p v-if="!($slots['panel-subtitle'] || panelSubtitle) && panelText" class="sk-panel-subtitle">
              {{ panelText }}
            </p>
          </div>
        </div>

        <slot></slot>
      </div>

      <slot v-else></slot>
    </div>

    <span slot="footer" :class="footerClassComputed">
      <slot name="footer">
        <el-button
          v-if="showCancel"
          class="btn-cancel"
          :type="cancelType"
          :disabled="cancelDisabled"
          @click="handleCancel"
        >
          <i v-if="cancelIcon" :class="cancelIcon"></i>
          <span>{{ cancelText }}</span>
        </el-button>

        <el-button
          v-if="showConfirm"
          class="btn-save"
          :type="confirmType"
          :loading="confirmLoading"
          :disabled="confirmDisabled"
          @click="handleConfirm"
        >
          <i v-if="confirmIcon" :class="confirmIcon"></i>
          <span>{{ confirmText }}</span>
        </el-button>
      </slot>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: "DialogBase",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    beforeClose: {
      type: Function,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
    width: {
      type: String,
      default: "720px",
    },
    top: {
      type: String,
      default: "8vh",
    },
    closeOnClickModal: {
      type: Boolean,
      default: false,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    destroyOnClose: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "",
    },
    dialogClass: {
      type: String,
      default: "",
    },
    contentClass: {
      type: String,
      default: "",
    },
    footerClass: {
      type: String,
      default: "",
    },
    contentVariant: {
      type: String,
      default: "",
    },
    usePanel: {
      type: Boolean,
      default: true,
    },
    panelVariant: {
      type: String,
      default: "",
    },
    panelTitle: {
      type: String,
      default: "",
    },
    panelSubtitle: {
      type: String,
      default: "",
    },
    panelText: {
      type: String,
      default: "",
    },
    panelIcon: {
      type: String,
      default: "",
    },
    panelIconTone: {
      type: String,
      default: "",
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    footerAlign: {
      type: String,
      default: "right",
    },
    showCancel: {
      type: Boolean,
      default: true,
    },
    cancelText: {
      type: String,
      default: "Dong",
    },
    cancelType: {
      type: String,
      default: "danger",
    },
    cancelIcon: {
      type: String,
      default: "el-icon-circle-close",
    },
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    closeOnCancel: {
      type: Boolean,
      default: true,
    },
    showConfirm: {
      type: Boolean,
      default: true,
    },
    confirmText: {
      type: String,
      default: "Luu",
    },
    confirmType: {
      type: String,
      default: "success",
    },
    confirmIcon: {
      type: String,
      default: "el-icon-circle-check",
    },
    confirmLoading: {
      type: Boolean,
      default: false,
    },
    confirmDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    dialogClassComputed() {
      const classList = ["sk-dialog"];
      if (this.size) {
        classList.push(`sk-dialog--${this.size}`);
      }
      if (this.dialogClass) {
        classList.push(this.dialogClass);
      }
      return classList.join(" ").trim();
    },
    contentClassComputed() {
      const classList = ["sk-dialog-content"];
      if (this.contentVariant) {
        classList.push(`sk-dialog-content--${this.contentVariant}`);
      }
      if (this.contentClass) {
        classList.push(this.contentClass);
      }
      return classList.join(" ").trim();
    },
    panelVariantClass() {
      return this.panelVariant ? `sk-panel--${this.panelVariant}` : "";
    },
    panelIconToneClass() {
      return this.panelIconTone ? `sk-panel-icon--${this.panelIconTone}` : "";
    },
    showPanelHead() {
      return !!(
        this.$slots["panel-icon"] ||
        this.$slots["panel-title"] ||
        this.$slots["panel-subtitle"] ||
        this.panelTitle ||
        this.panelSubtitle ||
        this.panelText ||
        this.panelIcon
      );
    },
    showPanelIcon() {
      return !!(this.$slots["panel-icon"] || this.panelIcon);
    },
    footerClassComputed() {
      if (!this.showFooter) {
        return "display-none";
      }
      const classList = ["sk-dialog-footer"];
      if (this.footerAlign === "between") {
        classList.push("sk-dialog-footer--between");
      } else if (this.footerAlign === "center") {
        classList.push("sk-dialog-footer--center");
      }
      if (this.footerClass) {
        classList.push(this.footerClass);
      }
      return classList.join(" ").trim();
    },
  },
  methods: {
    handleBeforeClose(done) {
      this.$emit("before-close", done);
      if (typeof this.beforeClose === "function") {
        this.beforeClose(done);
      } else if (this.$listeners["before-close"]) {
        return;
      } else {
        done();
      }
    },
    handleCancel() {
      this.$emit("cancel");
      if (this.closeOnCancel) {
        this.visibleProxy = false;
      }
    },
    handleConfirm() {
      this.$emit("confirm");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-dialog.scss";

.display-none {
  display: none;
}
</style>

